/**
 * Launch the MineZero client from any page — the launch-relevant slice
 * of what LauncherButton wires up, resolved through the SAME shared
 * launchService functions (resolveOrgApp / resolveLaunchVersion /
 * httpLaunch) so app, version and channel resolution cannot drift.
 * LauncherButton keeps its richer UI states (probe, downloads); folding
 * it onto this hook is a follow-up, not this change.
 *
 * First consumer: the Live Sessions Join button (observer launch). An
 * observer launch REQUIRES its args: the SpacetimeDB queue fallback
 * carries none (`queue_launch` has no args parameter), which would
 * start a normal trainee session — the wrong mode, silently. So
 * `launchWithArgs` uses the local launcher API only and reports the
 * launcher as unreachable instead of falling back.
 */
import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables as launcherTables } from '@sdk/launcher';
import {
  httpLaunch,
  resolveLaunchVersion,
  resolveOrgApp,
} from '@/services/launcher/launchService';
import { resolveChannelArgs } from '@/services/launcher/launcherArgs';
import { useLauncherChannel } from '@/hooks/useLauncherChannel';
import { useAuthKit } from '@/contexts/AuthProvider';
import { useStoredLaunchAppId } from '@/services/launcher/launchAppChoice';
import { useOrgSession } from '@/contexts/OrgContext';
import { useProjects } from '@/hooks/useProjects';
import { useTweaks } from '@/hooks/useTweaks';

const MINEZERO_APP_ID = import.meta.env.VITE_MINEZERO_APP_ID ?? 'minezero';
const STDB_URI = import.meta.env.VITE_STDB_CLOUD_URI;

export type LaunchWithArgsResult = {
  ok: boolean;
  /** ok only: the launcher is fetching the version before launching. */
  downloading?: boolean;
  /** failure only: the local API could not be reached at all. */
  unreachable?: boolean;
  /** failure only: user-facing reason. */
  message?: string;
};

export function useMineZeroLaunch(): {
  /** Launcher EXE running (live App connection) and the app row known. */
  canLaunch: boolean;
  launcherRunning: boolean;
  /** Context the arg builders need, resolved once here. */
  argContext: {
    orgName: string | null;
    serverUrl: string | null;
    workosOrgId: string | null;
    projectName: string | null;
    channelArgs: string;
  };
  /** Local-API-only launch with explicit args — never queue-falls-back. */
  launchWithArgs: (launchArgs: string) => Promise<LaunchWithArgsResult>;
} {
  const { organizationId } = useAuthKit();
  const { org } = useOrgSession();
  // Topbar's current site, same resolution as every site-scoped page.
  const { sites } = useProjects();
  const { tweaks } = useTweaks();
  const projectName = (sites[tweaks.siteIndex] ?? sites[0])?.projectName ?? null;
  const { channel } = useLauncherChannel();

  const [apps] = useTable('launcher', launcherTables.my_org_apps);
  const [appVersions] = useTable('launcher', launcherTables.my_org_app_versions);
  const [channelVersions] = useTable('launcher', launcherTables.my_org_channel_versions);
  const [connectedClients] = useTable('launcher', launcherTables.my_connected_clients);
  const [channelConfigs] = useTable('launcher', launcherTables.app_channel_config);

  // Same resolution as LauncherButton, incl. the admin's remembered per-org
  // pick — a Join/Enter-lobby click must launch the same app the header
  // button would (South32 2026-09-21).
  const rememberedAppId = useStoredLaunchAppId(organizationId);
  const mineZeroApp = useMemo(
    () => resolveOrgApp(apps, MINEZERO_APP_ID, rememberedAppId),
    [apps, rememberedAppId],
  );
  const effectiveAppId = mineZeroApp?.appId ?? MINEZERO_APP_ID;
  const selectedVersion = useMemo(
    () => resolveLaunchVersion(appVersions, channelVersions, effectiveAppId, channel),
    [appVersions, channelVersions, effectiveAppId, channel],
  );
  const launcherRunning = useMemo(
    () => connectedClients.some((c) => c.clientType?.tag === 'App'),
    [connectedClients],
  );

  const argContext = useMemo(
    () => ({
      orgName: org.slug ?? null,
      serverUrl: STDB_URI ?? null,
      workosOrgId: organizationId ?? null,
      projectName,
      channelArgs: resolveChannelArgs(channelConfigs, effectiveAppId, channel),
    }),
    [org.slug, organizationId, projectName, channelConfigs, effectiveAppId, channel],
  );

  async function launchWithArgs(launchArgs: string): Promise<LaunchWithArgsResult> {
    if (!mineZeroApp) {
      return { ok: false, unreachable: false, message: 'MineZero app not configured for this org' };
    }
    const res = await httpLaunch({
      appId: effectiveAppId,
      exePath: mineZeroApp.executablePath ?? '',
      versionId: selectedVersion?.versionId,
      launchArgs,
    });
    if ('reason' in res) {
      return {
        ok: false,
        unreachable: Boolean(res.unreachable),
        message: res.unreachable
          ? 'The launcher is not running on this machine — start it from the topbar rocket, then Join again.'
          : res.reason,
      };
    }
    return { ok: true, downloading: res.status === 'downloading' };
  }

  return {
    canLaunch: launcherRunning && Boolean(mineZeroApp),
    launcherRunning,
    argContext,
    launchWithArgs,
  };
}
